import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import ShiftCard from '../components/ShiftCard';
import ShiftSelector from '../components/ShiftSelector';
import TaskInput from '../components/TaskInput';
import TaskList from '../components/TaskList';
import SyncStatus from '../components/SyncStatus';
import { FileText, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SYNC_INTERVAL = 3000; // 3 seconds — reliable across all browsers incl. iOS Safari

const ShiftLogPage = () => {
  const [selectedShift, setSelectedShift] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [currentDate, setCurrentDate] = useState('');
  const [isShiftEnded, setIsShiftEnded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const syncRef = useRef(null);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch all data from backend
  const fetchData = useCallback(async (showSyncing = true) => {
    if (!isOnline) return;
    
    try {
      if (showSyncing) setSyncing(true);
      const stateRes = await axios.get(`${API}/shift-state`);
      const currentShift = stateRes.data.selectedShift;
      setSelectedShift(currentShift);
      setIsShiftEnded(stateRes.data.isShiftEnded);
      
      // Fetch tasks for the current shift only
      if (currentShift) {
        const tasksRes = await axios.get(`${API}/tasks?shift=${currentShift}`);
        setTasks(tasksRes.data);
      } else {
        setTasks([]);
      }
      setLastSync(new Date());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [isOnline]);

  useEffect(() => {
    const now = new Date();
    const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    setCurrentDate(now.toLocaleDateString('en-GB', options));
    
    // Hide initial loader
    const loader = document.getElementById('initial-loader');
    if (loader) {
      loader.style.display = 'none';
    }
    
    // Initial fetch
    fetchData();
    
    // Auto-refresh for real-time sync with other users
    syncRef.current = setInterval(() => fetchData(false), SYNC_INTERVAL);
    
    // Also sync when tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    
    return () => {
      if (syncRef.current) clearInterval(syncRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchData]);

  const handleShiftSelect = async (shiftId) => {
    setSelectedShift(shiftId);
    setIsShiftEnded(false);
    try {
      // Update shift state (don't delete tasks - each shift keeps its own tasks)
      await axios.put(`${API}/shift-state?selectedShift=${shiftId}&isShiftEnded=false`);
      // Fetch tasks for the new shift
      const tasksRes = await axios.get(`${API}/tasks?shift=${shiftId}`);
      setTasks(tasksRes.data);
    } catch (err) {
      console.error('Error updating shift:', err);
    }
  };

  const handleAddTask = async (taskText) => {
    // Optimistic update for instant feedback
    const tempId = `temp-${Date.now()}`;
    const optimisticTask = { id: tempId, text: taskText, shift: selectedShift };
    setTasks(prev => [...prev, optimisticTask]);
    
    try {
      const response = await axios.post(`${API}/tasks`, {
        text: taskText,
        shift: selectedShift
      });
      // Replace temp task with real one
      setTasks(prev => prev.map(t => t.id === tempId ? response.data : t));
    } catch (err) {
      console.error('Error adding task:', err);
      // Remove optimistic task on error
      setTasks(prev => prev.filter(t => t.id !== tempId));
    }
  };

  const handleDeleteTask = async (taskId) => {
    // Optimistic update
    const previousTasks = tasks;
    setTasks(prev => prev.filter(task => task.id !== taskId));
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
    } catch (err) {
      console.error('Error deleting task:', err);
      setTasks(previousTasks); // Rollback on error
    }
  };

  const handleEditTask = async (taskId, newText) => {
    // Optimistic update
    const previousTasks = tasks;
    setTasks(prev =>
      prev.map(task =>
        task.id === taskId ? { ...task, text: newText } : task
      )
    );
    
    try {
      await axios.put(`${API}/tasks/${taskId}`, { text: newText });
    } catch (err) {
      console.error('Error editing task:', err);
      setTasks(previousTasks); // Rollback on error
    }
  };

  const handleGenerateReport = async () => {
    setIsShiftEnded(true);
    try {
      await axios.put(`${API}/shift-state?selectedShift=${selectedShift}&isShiftEnded=true`);
    } catch (err) {
      console.error('Error generating report:', err);
    }
  };

  const handleCopyReport = async () => {
    const report = tasks.map((task, index) => `${index + 1}. ${task.text}`).join('\n');
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClearReport = async () => {
    setIsShiftEnded(false);
    setCopied(false);
    setTasks([]);
    try {
      await Promise.all([
        axios.put(`${API}/shift-state?selectedShift=${selectedShift}&isShiftEnded=false`),
        axios.delete(`${API}/tasks?shift=${selectedShift}`)  // Only delete tasks for current shift
      ]);
    } catch (err) {
      console.error('Error clearing report:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-[#080c12] flex items-center justify-center">
        <div className="text-[#d4a636] font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#080c12] flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Offline indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white text-center py-2 text-sm font-mono z-50">
          You're offline. Changes will sync when back online.
        </div>
      )}
      
      {/* Background dot pattern */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}
      />
      
      {/* Subtle glow effect at top */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/5 blur-[100px] rounded-full" />
      
      <ShiftCard isExpanded={!!selectedShift}>
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-[0.1em] sm:tracking-[0.15em] text-[#d4a636] font-mono uppercase mb-1 sm:mb-2">
              SMARTSHIFT
            </h1>
            <p className="text-[10px] sm:text-xs tracking-[0.15em] sm:tracking-[0.25em] text-gray-500 font-mono uppercase mb-1 sm:mb-2">
              TEAM TASK SYNC & SHIFT REPORT
            </p>
            <p className="text-xs sm:text-sm text-gray-400 font-mono">
              {currentDate}
            </p>
            
            {/* Sync status - only show when shift is selected */}
            {selectedShift && (
              <div className="mt-2 sm:mt-3">
                <SyncStatus syncing={syncing} isOnline={isOnline} lastSync={lastSync} />
              </div>
            )}
          </div>
          
          {/* Divider */}
          {selectedShift && <div className="border-t border-gray-800 mb-3 sm:mb-4" />}
          
          {/* Shift Selector */}
          <div className="mb-3 sm:mb-4">
            <ShiftSelector
              selectedShift={selectedShift}
              onShiftSelect={handleShiftSelect}
              isExpanded={!!selectedShift}
            />
          </div>
          
          {/* Task Section - only show when shift is selected */}
          {selectedShift && (
            <>
              <div className="border-t border-gray-800 pt-3 sm:pt-4 mb-3 sm:mb-4">
                <TaskInput onAddTask={handleAddTask} />
              </div>
              
              <div className="border-t border-gray-800 pt-3 sm:pt-4">
                <TaskList
                  tasks={tasks}
                  onDeleteTask={handleDeleteTask}
                  onEditTask={handleEditTask}
                  isLocked={isShiftEnded}
                />
              </div>
              
              {/* Generate Report Button */}
              {tasks.length > 0 && !isShiftEnded && (
                <div className="border-t border-gray-800 pt-3 sm:pt-4 mt-3 sm:mt-4">
                  <button
                    onClick={handleGenerateReport}
                    data-testid="generate-report-button"
                    className="w-full px-4 py-3 sm:py-3 bg-[#d4a636] hover:bg-[#c49526] active:bg-[#b38520] text-[#0a1018] font-mono font-semibold rounded flex items-center justify-center gap-2 transition-colors touch-manipulation"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="text-sm sm:text-base">Generate Shift Report</span>
                  </button>
                </div>
              )}
              
              {/* Shift Ended Message */}
              {isShiftEnded && (
                <div className="border-t border-gray-800 pt-3 sm:pt-4 mt-3 sm:mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-xs tracking-[0.2em] text-gray-400 font-mono uppercase">SHIFT REPORT</span>
                  </div>
                  
                  <div className="bg-[#0d1520] border border-gray-700 rounded p-3 sm:p-4 mb-3 max-h-40 sm:max-h-60 overflow-y-auto">
                    <pre className="font-mono text-xs sm:text-sm text-gray-200 whitespace-pre-wrap">
{tasks.map((task, index) => `${index + 1}. ${task.text}`).join('\n')}
                    </pre>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyReport}
                      data-testid="copy-report-button"
                      className="flex-1 px-3 sm:px-4 py-2 sm:py-2 bg-[#d4a636] hover:bg-[#c49526] active:bg-[#b38520] text-[#0a1018] font-mono text-xs sm:text-sm font-semibold rounded flex items-center justify-center gap-2 transition-colors touch-manipulation"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy Report</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleClearReport}
                      data-testid="clear-report-button"
                      className="px-3 sm:px-4 py-2 sm:py-2 bg-transparent hover:bg-[#243040] active:bg-[#2a3a50] border border-gray-600 text-gray-300 font-mono text-xs sm:text-sm rounded transition-colors touch-manipulation"
                    >
                      Clear Report
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Footer - only show when no shift selected */}
          {!selectedShift && (
            <>
              <div className="border-t border-gray-800 mt-3 sm:mt-4 pt-3 sm:pt-4" />
              <p className="text-center text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.15em] text-gray-600 font-mono uppercase">
                MULTI-SKILLS ENGINEERING TEAM • SHIFT HANDOVER TOOL
              </p>
            </>
          )}
        </div>
      </ShiftCard>
      
      {/* Emergent Badge - using HTML badge from index.html */}
    </div>
  );
};

export default ShiftLogPage;
